import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { db } from "../firebase/config";
import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "./AuthContext";

// New design: store goal metadata under users/{uid}/meta/goals
// Progress is computed client-side by subscribing to finance/data, dashboardTasks and habits.

const GoalsContext = createContext();

export function GoalsProvider({ children }) {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;

  const [goals, setGoals] = useState([]); // raw goal docs
  const [computed, setComputed] = useState({}); // map goalId -> computed metrics
  const [loading, setLoading] = useState(true);

  // Subscribe to goals metadata under users/{uid}/meta/goals
  useEffect(() => {
    if (!uid) return;
    const goalsCol = collection(db, "users", uid, "goals");
    const unsub = onSnapshot(
      goalsCol,
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setGoals(items);
        setLoading(false);
      },
      (err) => {
        console.error("Goals listener error", err);
      }
    );
    return () => unsub();
  }, [uid]);

  // Helper: listen to finance/data totals
  const [totals, setTotals] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    totalAllocated: 0,
  });
  useEffect(() => {
    if (!uid) return;
    const dataRef = doc(db, "users", uid, "finance", "data");
    const unsub = onSnapshot(dataRef, (snap) => {
      if (!snap.exists())
        return setTotals({
          totalIncome: 0,
          totalExpenses: 0,
          totalAllocated: 0,
        });
      const t = snap.data().totals || {};
      setTotals({
        totalIncome: t.totalIncome || 0,
        totalExpenses: t.totalExpenses || 0,
        totalAllocated: t.totalAllocated || 0,
      });
    });
    return () => unsub();
  }, [uid]);

  // Listener: dashboard tasks (simple snapshot to count completed tasks)
  const [taskCounts, setTaskCounts] = useState({ total: 0, completed: 0 });
  useEffect(() => {
    if (!uid) return;
    const tasksCol = collection(db, "users", uid, "dashboardTasks");
    const unsub = onSnapshot(tasksCol, (snap) => {
      let total = 0,
        completed = 0;
      snap.docs.forEach((d) => {
        total += 1;
        const data = d.data() || {};
        if (data.completed) completed += 1;
        else if (data.completedDates && Object.keys(data.completedDates).length)
          completed += 1;
      });
      setTaskCounts({ total, completed });
    });
    return () => unsub();
  }, [uid]);

  // Listener: habits to count check-ins
  const [habitCheckIns, setHabitCheckIns] = useState(0);
  useEffect(() => {
    if (!uid) return;
    const habitsCol = collection(db, "users", uid, "habits");
    const unsub = onSnapshot(habitsCol, (snap) => {
      let checkIns = 0;
      snap.docs.forEach((d) => {
        const data = d.data() || {};
        const cd = data.completedDates || {};
        checkIns += Object.keys(cd).length;
      });
      setHabitCheckIns(checkIns);
    });
    return () => unsub();
  }, [uid]);

  // Recompute derived goal metrics when underlying sources change
  useEffect(() => {
    if (!goals || !Array.isArray(goals)) return;
    const map = {};
    goals.forEach((g) => {
      const type = (g.type || "PERSONAL").toUpperCase();
      let current = 0;
      let pct = 0;
      if (type === "FINANCIAL") {
        const saved = (totals.totalIncome || 0) - (totals.totalExpenses || 0);
        current = saved;
        pct = g.target_value ? Math.round((saved / g.target_value) * 100) : 0;
      } else if (type === "TASK") {
        current = taskCounts.completed || 0;
        pct = g.target_count ? Math.round((current / g.target_count) * 100) : 0;
      } else if (type === "HABIT") {
        current = habitCheckIns || 0;
        pct = g.target_count ? Math.round((current / g.target_count) * 100) : 0;
      } else {
        current = Number(g.current_value || 0);
        pct = g.target_value ? Math.round((current / g.target_value) * 100) : 0;
      }

      pct = Math.max(0, Math.min(100, pct));

      // simple status/message heuristics
      let status = "active";
      let message = "Keep going";
      if (pct >= 100) {
        status = "completed";
        message = "Goal completed";
      } else if (g.deadline) {
        try {
          const daysLeft = Math.ceil(
            (new Date(g.deadline).getTime() - Date.now()) /
              (1000 * 60 * 60 * 24)
          );
          if (daysLeft <= 0) message = "Passed deadline — act now";
          else if (daysLeft <= 3) message = "Approaching deadline";
          else if (pct > 70) message = "Approaching goal";
          else if (pct < 25) message = "Behind schedule";
          else message = "On track";
        } catch (e) {
          message = pct > 70 ? "Approaching goal" : "Keep going";
        }
      } else {
        if (pct > 85) message = "Almost there";
        else if (pct > 50) message = "Making good progress";
        else if (pct < 25) message = "Getting started";
      }

      map[g.id] = { current, pct, status, message };
    });
    setComputed(map);
  }, [goals, totals, taskCounts, habitCheckIns]);

  // CRUD helpers using users/{uid}/meta/goals path
  const createGoal = async ({
    title,
    type = "PERSONAL",
    target_value = 0,
    target_count = 0,
    deadline = null,
    milestones = [],
  }) => {
    if (!uid) throw new Error("Not signed in");
    const col = collection(db, "users", uid, "goals");
    const payload = {
      name: title,
      type: type.toUpperCase(),
      target_value: Number(target_value || 0),
      target_count: Number(target_count || 0),
      current_value: 0,
      milestones: milestones || [],
      deadline: deadline || null,
      is_completed: false,
      createdAt: serverTimestamp(),
    };
    const ref = await addDoc(col, payload);
    return { id: ref.id, ...payload };
  };

  const updateGoal = async (id, patch) => {
    if (!uid) throw new Error("Not signed in");
    const gRef = doc(db, "users", uid, "goals", id);
    await updateDoc(gRef, patch);
  };

  const deleteGoal = async (id) => {
    if (!uid) throw new Error("Not signed in");
    const gRef = doc(db, "users", uid, "goals", id);
    await deleteDoc(gRef);
  };

  const value = useMemo(
    () => ({ goals, computed, loading, createGoal, updateGoal, deleteGoal }),
    [goals, computed, loading]
  );

  return (
    <GoalsContext.Provider value={value}>{children}</GoalsContext.Provider>
  );
}

export const useGoals = () => useContext(GoalsContext);

export default GoalsContext;
